import { trackEvent } from "../utils/analytics"
import {
  recordPwaInstallationIfNeeded,
  wirePwaInstallationRecorder,
} from "./pwaInstallationRecorder"
import { CONNECTIVITY_CHANGE, hasInternetAccess, seemsOffline, visit } from "../utils/connectivity";
import { isPwaEnabled } from "../utils/offline_gating";
import { PUSH_SUBSCRIPTION_OWNER_KEY, getActivePushSubscription } from "./pushSubscription";

// Registers the service worker and wires basic online/offline UI indicators.
// This runs on every Turbo page load and is safe to call repeatedly.
let swRegistration = null;
let blankLinkHandlerAttached = false;
let notificationClickListenerAttached = false;
let logoutCleanupHandlerAttached = false;

function isStandalonePwa() {
  // Supports Chrome/Edge (matchMedia) and iOS (navigator.standalone)
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function handleBlankTargetClicks(event) {
  if (!isStandalonePwa()) return;
  if (event.defaultPrevented) return;

  const anchor = event.target.closest?.('a[target="_blank"]');
  if (!anchor) return;

  const href = anchor.href;
  if (!href) return;

  // Ignore non-http(s) schemes so mailto/tel continue to work normally
  const protocol = href.split(":")[0];
  if (protocol && !protocol.match(/^https?$/i)) return;

  if (seemsOffline()) return;

  event.preventDefault();
  event.stopPropagation();

  if ("pwaSameWindow" in anchor.dataset) {
    visit(href);
    return;
  }

  window.open(href, "_blank", "noopener,noreferrer");
}

function wireBlankTargetHandling() {
  if (blankLinkHandlerAttached) return;
  document.addEventListener("click", handleBlankTargetClicks, true);
  blankLinkHandlerAttached = true;
}

function notifyUpdateAvailable() {
  document.dispatchEvent(new CustomEvent("sw:update-available"));
}

function checkForUpdate() {
  if (!swRegistration) return;
  swRegistration.update().catch((err) => {
    console.warn("Service worker update check failed:", err);
  });
}


function disableServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;

  swRegistration = null;

  navigator.serviceWorker
    .getRegistrations()
    .then(async (registrations) => {
      for (const registration of registrations) {
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) registration.unregister();
      }
    })
    .catch(() => {
      // Swallow errors; the goal is simply to avoid keeping stale registrations
    });

  const offlineIndicator = document.getElementById("offline-indicator");
  if (offlineIndicator) {
    offlineIndicator.hidden = true;
  }

  if (window.__aquiferStatusPoll) {
    clearInterval(window.__aquiferStatusPoll);
    window.__aquiferStatusPoll = null;
  }
}

function registerServiceWorker() {
  if (!isPwaEnabled()) return;
  if (swRegistration) return;
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker
    .register("/service-worker.js")
    .then((registration) => {
      if (!isPwaEnabled()) return;
      swRegistration = registration;

      registration.onupdatefound = () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.onstatechange = () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            notifyUpdateAvailable();
          }
        };
      };
    })
    .catch((err) => {
      // Log to console only; do not block the page
      console.warn("Service worker registration failed:", err);
    });
}

// The online/offline events are reliable edge signals, unlike the
// navigator.onLine level. Persist them synchronously so a reload right after
// reconnecting can't read a stale flag (the async ping below would lose that
// race); the ping then confirms or corrects within moments.
function recordConnectivityEdge(event) {
  sessionStorage.setItem("aquifer-online", event.type === "online" ? "1" : "0");
}

let pageHidden = false;

function wireStatusListeners() {
  window.addEventListener("pagehide", () => { pageHidden = true; });
  window.addEventListener("pageshow", () => { pageHidden = false; });
  window.addEventListener("online", recordConnectivityEdge);
  window.addEventListener("offline", recordConnectivityEdge);
  window.addEventListener("online", confirmReconnection);
  window.addEventListener("offline", updateStatusIndicators);
}

let onlineOfflineListenersWired = false;

function postFlushOfflineHitsToServiceWorker() {
  if (!("serviceWorker" in navigator)) return;

  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage("flushOfflineHits");
    return;
  }

  navigator.serviceWorker.ready.then((registration) => {
    if (registration.active) {
      registration.active.postMessage("flushOfflineHits");
    }
  });
}

function flushOfflineHitsOnReconnect() {
  if ("serviceWorker" in navigator && "SyncManager" in window) {
    navigator.serviceWorker.ready.then((reg) => {
      reg.sync.register("flushOfflineHits").catch((err) => {
        console.warn("Background sync registration failed:", err);
      });
    });
  } else {
    postFlushOfflineHitsToServiceWorker();
  }
}

const RECONNECT_RETRY_MS = 1000;

async function confirmReconnection() {
  const isOnline = await updateStatusIndicators();
  if (isOnline) return;
  setTimeout(updateStatusIndicators, RECONNECT_RETRY_MS);
}

async function updateStatusIndicators() {
  const offlineIndicator = document.getElementById("offline-indicator");

  const isOnline = await hasInternetAccess();
  await new Promise((resolve) => setTimeout(resolve));
  if (pageHidden) return isOnline;

  sessionStorage.setItem("aquifer-online", isOnline ? "1" : "0");

  if (offlineIndicator) {
    offlineIndicator.hidden = isOnline;
  }
  document.dispatchEvent(new CustomEvent(CONNECTIVITY_CHANGE, { detail: { online: isOnline } }));
  return isOnline;
}

/** When PWA is off, still keep the layout offline banner in sync (one guarded wire for the whole session). */
let globalOfflineIndicatorListenersWired = false;

function wireGlobalOfflineIndicatorWhenNotPwa() {
  if (globalOfflineIndicatorListenersWired) return;
  const offlineIndicator = document.getElementById("offline-indicator");
  if (!offlineIndicator) return;

  globalOfflineIndicatorListenersWired = true;
  updateStatusIndicators();
  wireStatusListeners();
}

function timeToClickMs(sentAt, clickedAt) {
  if (!sentAt || !clickedAt) return undefined;
  var delta = new Date(clickedAt) - new Date(sentAt);
  return delta > 0 ? delta : undefined;
}

function wireNotificationClickListener() {
  if (!("serviceWorker" in navigator)) return;
  if (notificationClickListenerAttached) return;
  notificationClickListenerAttached = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (!event.data || !event.data.type) return;

    if (event.data.type === "NOTIFICATION_CLICKED") {
      const timeToClick = timeToClickMs(event.data.sent_at, event.data.clicked_at);

      // Track via server-side endpoint using sendBeacon for reliability during page navigation
      const clickData = {
        title: event.data.title,
        url: event.data.url,
        clicked_at: event.data.clicked_at,
        time_to_click_ms: timeToClick
      };
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(clickData)], { type: 'application/json' });
        navigator.sendBeacon('/push_subscription/track_click?authenticity_token=' + encodeURIComponent(csrfToken || ''), blob);
      } else {
        fetch('/push_subscription/track_click', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
          body: JSON.stringify(clickData),
          keepalive: true
        }).catch(() => {});
      }
    } else if (event.data.type === "NOTIFICATION_DISMISSED") {
      trackEvent("push_notification_dismissed", {
        push_notification_title: event.data.title,
        push_notification_url: event.data.url,
        dismissed_at: event.data.dismissed_at
      });
    } else if (event.data.type === "SW_ERROR") {
      trackEvent("push_service_worker_error", {
        error_message: event.data.message,
        error_filename: event.data.filename,
        error_lineno: event.data.lineno
      });
    }
  });
}

function wireOnlineOfflineListeners() {
  if (navigator.onLine) {
    postFlushOfflineHitsToServiceWorker();
  }
  updateStatusIndicators();

  if (!window.__aquiferStatusPoll) {
    window.__aquiferStatusPoll = setInterval(updateStatusIndicators, 2000);
  }

  if (onlineOfflineListenersWired) return;
  onlineOfflineListenersWired = true;

  wireStatusListeners();
  window.addEventListener("online", flushOfflineHitsOnReconnect);
}

function isUserLoggedIn() {
  return document.body?.dataset?.userLoggedIn === "true";
}

function currentUserId() {
  return document.body?.dataset?.userId || "";
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || "";
}

async function syncPushSubscriptionToCurrentUser() {
  if (!isUserLoggedIn()) return;
  const userId = currentUserId();
  if (!userId) return;
  if (sessionStorage.getItem("pushSubscriptionSyncedForUser") === userId) return;

  try {
    const subscription = await getActivePushSubscription();
    if (!subscription) return;

    const owner = localStorage.getItem(PUSH_SUBSCRIPTION_OWNER_KEY);
    if (owner !== userId) {
      await subscription.unsubscribe();
      localStorage.removeItem(PUSH_SUBSCRIPTION_OWNER_KEY);
      return;
    }

    const key = subscription.getKey?.("p256dh");
    const auth = subscription.getKey?.("auth");
    if (!key || !auth) return;

    sessionStorage.setItem("pushEndpoint", subscription.endpoint);

    const toBase64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));

    const response = await fetch("/push_subscription", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({
        push_subscription: {
          endpoint: subscription.endpoint,
          p256dh_key: toBase64(key),
          auth_key: toBase64(auth)
        }
      })
    });

    if (response.ok) {
      sessionStorage.setItem("pushSubscriptionSyncedForUser", userId);
    }
  } catch {
    // Intentionally do not set the synced flag so the next boot retries
  }
}

function handleLogoutClick(event) {
  const link = event.target.closest?.('a[data-push-cleanup]');
  if (!link) return;

  sessionStorage.removeItem("pushSubscriptionSyncedForUser");

  const endpoint = sessionStorage.getItem("pushEndpoint");
  if (!endpoint) return;

  // Thread the endpoint through the sign-out request itself so the server
  // destroys the row atomically before the Devise session is torn down.
  // Avoids the race where a separate DELETE would arrive post-session-destroy.
  const url = new URL(link.href, window.location.origin);
  url.searchParams.set("push_endpoint", endpoint);
  link.href = url.toString();
  sessionStorage.removeItem("pushEndpoint");
}

function wireLogoutPushCleanup() {
  if (logoutCleanupHandlerAttached) return;
  document.addEventListener("click", handleLogoutClick, true);
  logoutCleanupHandlerAttached = true;
}

function isPushNotificationsEnabled() {
  const meta = document.querySelector('meta[name="push-notifications-enabled"]');
  return meta && meta.content === "true";
}

const boot = () => {
  wirePwaInstallationRecorder();
  recordPwaInstallationIfNeeded();
  if (isPushNotificationsEnabled()) {
    wireNotificationClickListener();
    wireLogoutPushCleanup();
    syncPushSubscriptionToCurrentUser();
  }
  if (!isPwaEnabled()) {
    disableServiceWorkers();
    wireGlobalOfflineIndicatorWhenNotPwa();
    return;
  }

  registerServiceWorker();
  checkForUpdate();
  wireOnlineOfflineListeners();
  wireBlankTargetHandling();
};

boot();

document.addEventListener("turbo:load", boot);
document.addEventListener("DOMContentLoaded", boot);

